// Onboarding View - Main wrapper switching between Question, Result, and Strategy views
// Receives ViewModel props (not Model), uses dispatchers for navigation

import { OnboardingVM } from '../view-model/Onboarding.viewModel';
import { getPreviousStep } from '../view-model/OnboardingStep.viewModel';
import { ModalityView } from './Modality.view';
import { RevenueGoalView } from './RevenueGoal.view';
import { QuestionView } from './Question.view';
import { ResultView } from './Result.view';
import { StrategyView } from './Strategy.view';
import { useBookCallViewModel } from '../book-call/view-model/BookCall.viewModel';
import { BookCallAsyncView } from '../book-call/view/BookCall.view';
import { CalculatingStrategy } from './components/CalculatingStrategy';

// Self-contained booking step: owns its own model/view-model (the backend
// gates qualification), so it is rendered via a dedicated container that
// drives its own hook rather than the Onboarding view-model.
const BookCallStep = () => <BookCallAsyncView vm={useBookCallViewModel()} />;

export type OnboardingViewProps = OnboardingVM;

export const OnboardingView = (vm: OnboardingViewProps) => {
  const canGoBack = getPreviousStep(vm.currentStep, vm.totalSteps) !== null;

  // Waiting room after strategy selection while the booking classification
  // polls for the enrichment verdict; routes to book-call or the guide once
  // resolved (or on timeout).
  if (vm.isCalculating) {
    return <CalculatingStrategy />;
  }

  // Qualified-creator booking step (between strategy selection and the guide).
  if (vm.isBookCallStep) {
    return <BookCallStep />;
  }

  // Modality view when on modality step
  if (vm.isModalityStep) {
    return (
      <ModalityView
        initialKeywords={vm.modalityInitialSelection}
        onSubmit={(keywords) => vm.dispatch('SubmitModality', keywords)}
        onBack={vm.lazyDispatch('GoBack')}
        canGoBack={canGoBack}
        displayStep={vm.currentDisplayStep ?? 1}
        totalDisplaySteps={vm.totalDisplaySteps}
      />
    );
  }

  // Revenue-goal slider when on revenue-goal step
  if (vm.isRevenueGoalStep) {
    return (
      <RevenueGoalView
        initialPosition={vm.revenueGoalInitialPosition}
        onSubmit={(position) => vm.dispatch('SubmitRevenueGoal', position)}
        onBack={vm.lazyDispatch('GoBack')}
        canGoBack={canGoBack}
      />
    );
  }

  // Strategy view when on strategy step
  if (vm.isStrategyStep) {
    return (
      <StrategyView
        strategies={vm.strategies}
        caseStudies={vm.allCaseStudies}
        isSaving={vm.isSaving}
        isReviewMode={vm.isReviewMode}
        dispatch={vm.dispatch}
        lazyDispatch={vm.lazyDispatch}
      />
    );
  }

  // Result view when on result step (between questions and strategy)
  if (vm.isResultStep) {
    return (
      <ResultView
        avatarUrls={vm.resultAvatarUrls}
        isSaving={vm.isSaving}
        dispatch={vm.dispatch}
        lazyDispatch={vm.lazyDispatch}
      />
    );
  }

  // Question view when current question exists.
  if (vm.currentQuestion && vm.currentQuestionIndex !== null) {
    return (
      <QuestionView
        question={vm.currentQuestion}
        currentStep={vm.currentDisplayStep ?? vm.currentQuestionIndex + 1}
        totalSteps={vm.totalDisplaySteps}
        canGoBack={canGoBack}
        isSaving={vm.isSaving}
        dispatch={vm.dispatch}
        lazyDispatch={vm.lazyDispatch}
      />
    );
  }

  // Fallback (should not happen in normal flow)
  return null;
};
